import type { DesignSpec } from "../types/designSpec";
import { getDesignSpecReferencesForSide } from "./agent/designSpec";

export function getUploadedImageReferences(
  uploadedImages?: File[],
  uploadedImageUrls?: string[],
  designSpec?: DesignSpec,
  side: "front" | "back" = "front"
): File[] | string[] | undefined {
  const validUploadedImages =
    uploadedImages?.filter(
      (image): image is File =>
        typeof File !== "undefined" && image instanceof File
    ) ?? [];
  const validUploadedImageUrls =
    uploadedImageUrls
      ?.filter((url): url is string => typeof url === "string")
      .map(url => url.trim())
      .filter(Boolean) ?? [];

  if (designSpec) {
    const contractReferences = getDesignSpecReferencesForSide(designSpec, side)
      .map(reference => reference.url.trim())
      .filter(Boolean);
    // Once the customer has reviewed a DesignSpec snapshot, its ordered,
    // side-scoped references are authoritative. Do not silently reintroduce
    // stale browser form uploads that the customer removed from the brief.
    if (contractReferences.length > 0) return contractReferences;

    // Anonymous/new uploads can exist only as ephemeral data URLs or File
    // objects and therefore cannot be serialized into DesignSpec. Preserve
    // those pixels. Hosted URLs, however, are durable contract references: if
    // none remain in the spec, they were removed and must stay removed.
    const ephemeralUrls = validUploadedImageUrls.filter(url =>
      url.startsWith("data:")
    );
    if (
      validUploadedImages.length > 0 &&
      (ephemeralUrls.length > 0 || validUploadedImageUrls.length === 0)
    ) {
      return validUploadedImages;
    }
    return ephemeralUrls.length > 0 ? ephemeralUrls : undefined;
  }

  if (validUploadedImages.length > 0) {
    return validUploadedImages;
  }

  if (validUploadedImageUrls.length > 0) {
    return validUploadedImageUrls;
  }

  return undefined;
}

/** /api/generate-image rejects prompts outside this range. */
const MIN_GENERATION_PROMPT_LENGTH = 10;
const MAX_GENERATION_PROMPT_LENGTH = 1000;

/**
 * Anchors a very short edit request to the design it edits.
 *
 * "red", "bigger" and "Mermaid" are complete instructions from the customer's
 * point of view, but they are under the endpoint's minimum prompt length, so
 * they used to come back as a validation error the customer could only escape
 * by rewording — prod shows one session hitting that error eight times in a
 * row. Restating the request against the existing design keeps their exact
 * words and hands the image model the context those words assume.
 */
export function expandShortModificationPrompt(
  request: string,
  context: {
    side: "front" | "back";
    basePrompt?: string;
    attributes: Record<string, string | undefined>;
  }
): string {
  if (request.length >= MIN_GENERATION_PROMPT_LENGTH) return request;

  const compact = (value: string | undefined): string =>
    value?.replace(/\s+/g, " ").trim() ?? "";
  const attributes = Object.entries(context.attributes)
    .map(([field, value]) =>
      compact(value) ? `${field} ${compact(value)}` : ""
    )
    .filter(Boolean)
    .join(", ");
  const existingDesign = compact(context.basePrompt) || attributes;

  return [
    `Apply this change to the ${context.side} view: ${request}.`,
    existingDesign
      ? `Keep the rest of the existing design: ${existingDesign}.`
      : "",
  ]
    .filter(Boolean)
    .join(" ")
    .slice(0, MAX_GENERATION_PROMPT_LENGTH);
}
